import React, { useMemo } from 'react';
import { formatInr } from './perfHubEconomicsFormat';
import PerfHubSectionInfo from './PerfHubSectionInfo';
import { PERF_HUB_OVERVIEW_HELP } from './perfHubOverviewHelp';
import { downloadCampaignEconomicsCsv } from './perfHubEconomicsExport';

const KPI_DEFS = [
  {
    key: 'spend_inr',
    label: 'Tracked spend',
    hint: 'Creator fees total, or campaign budget',
    format: (v) => formatInr(v, { compact: true }),
    full: (v) => formatInr(v),
    accent: 'spend',
  },
  {
    key: 'cost_per_1k_reach_inr',
    label: '₹ / 1K reach',
    hint: 'Spend ÷ reach (per thousand)',
    format: (v) => formatInr(v, { compact: true }),
    full: (v) => formatInr(v),
    accent: 'reach',
  },
  {
    key: 'cpe_engagement_inr',
    label: 'Cost per engagement',
    hint: 'Spend ÷ engagements',
    format: (v) => formatInr(v, { compact: true }),
    full: (v) => formatInr(v),
    accent: 'engagement',
  },
  {
    key: 'cpm_impressions_inr',
    label: 'CPM',
    hint: 'Cost per 1,000 impressions',
    format: (v) => formatInr(v, { compact: true }),
    full: (v) => formatInr(v),
    accent: 'impressions',
  },
];

function Kpi({ def, summary }) {
  const raw = summary?.[def.key];
  const configured = summary?.configured;
  const hasValue = configured && raw != null;
  const display = hasValue ? def.format(raw) : '—';
  const fullDisplay = hasValue && typeof def.full === 'function' ? def.full(raw) : null;
  const isAbbreviated = !!fullDisplay && fullDisplay !== display;
  return (
    <div className={`ph-econ-kpi ph-econ-kpi--${def.accent}`}>
      <span className="ph-econ-kpi-label">{def.label}</span>
      <span
        className="ph-econ-kpi-value"
        title={isAbbreviated ? fullDisplay : undefined}
      >
        {display}
      </span>
      <span className="ph-econ-kpi-hint">{def.hint}</span>
    </div>
  );
}

/**
 * Campaign ROI strip — uses dashboard `economics.summary` from API.
 */
export default function PerfHubCampaignEconomics({
  economics,
  variant = 'full',
  onEditBudget,
  showEdit = true,
  campaignName,
  exportEnabled = true,
}) {
  const summary = economics?.summary;
  const configured = summary?.configured;
  const perCreator = economics?.per_creator || [];
  const canDownload = exportEnabled && configured && perCreator.length > 0;

  const handleDownload = () => {
    if (!canDownload) return;
    downloadCampaignEconomicsCsv(economics, { campaignName });
  };

  const sourceLabel = useMemo(() => {
    if (!configured) return null;
    if (summary?.spend_source === 'creator_fees') return 'Spend from creator fees';
    if (summary?.spend_source === 'campaign_budget') return 'Spend from campaign budget';
    return null;
  }, [configured, summary?.spend_source]);

  if (variant === 'compact' && !configured) return null;

  return (
    <section
      className={`ph-econ-panel ${variant === 'compact' ? 'ph-econ-panel--compact' : ''}`}
      aria-labelledby="ph-econ-heading"
    >
      <div className="ph-econ-head">
        <div>
          <h4 id="ph-econ-heading" className="ph-econ-title">
            <span className="ph-econ-title-icon" aria-hidden>
              💰
            </span>
            Campaign ROI
            <PerfHubSectionInfo label="Campaign ROI">
              {PERF_HUB_OVERVIEW_HELP.campaignRoi}
            </PerfHubSectionInfo>
          </h4>
        </div>
        <div className="ph-econ-head-actions">
          {configured && (
            <button
              type="button"
              className="btn-outline ph-econ-download-btn"
              disabled={!canDownload}
              onClick={handleDownload}
              title={
                !exportEnabled
                  ? 'CSV export requires a paid plan'
                  : perCreator.length === 0
                    ? 'No per-creator ROI rows yet'
                    : 'Download Campaign ROI summary and per-creator breakdown'
              }
            >
              Download CSV {!exportEnabled ? '(paid)' : ''}
            </button>
          )}
          {showEdit && onEditBudget && (
            <button type="button" className="btn-outline ph-econ-edit-btn" onClick={onEditBudget}>
              {configured ? 'Edit spend' : 'Add spend'}
            </button>
          )}
        </div>
      </div>

      {!configured ? (
        <div className="ph-econ-empty">
          <p>
            No spend configured yet. Enter your <strong>campaign budget</strong> or{' '}
            <strong>per-creator fees</strong> to see ₹ per 1K reach and cost per engagement.
          </p>
          {showEdit && onEditBudget && (
            <button type="button" className="btn-primary ph-btn-glow" onClick={onEditBudget}>
              Set campaign spend
            </button>
          )}
        </div>
      ) : (
        <>
          {sourceLabel && (
            <p className="ph-econ-source">
              {sourceLabel}
              {summary?.campaign_budget_inr != null && summary?.spend_source === 'campaign_budget' && (
                <> · Budget {formatInr(summary.campaign_budget_inr)}</>
              )}
            </p>
          )}
          <div className="ph-econ-kpi-grid">
            {KPI_DEFS.map((def) => (
              <Kpi key={def.key} def={def} summary={summary} />
            ))}
          </div>
          {summary?.allocation_note && variant === 'full' && (
            <p className="ph-econ-note">{summary.allocation_note}</p>
          )}
        </>
      )}
    </section>
  );
}
